import { useState } from "react";
import { contactEmail, siteName } from "@/mocks/site";

const FORM_ACTION = "https://readdy.ai/api/form/dahhtaboh653ivfvnvvg";
const MESSAGE_LIMIT = 500;

type Status = "idle" | "loading" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const contactChannels = [
  { icon: "ri-mail-line", label: "线索投稿", value: contactEmail },
  { icon: "ri-survey-line", label: "内容纠错", value: "请在表单中注明具体篇目标题" },
  { icon: "ri-briefcase-4-line", label: "商务与合作", value: "合作类型仅限内容授权与栏目共建" },
];

export default function AboutContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const raw = new FormData(form);

    const trap = String(raw.get("contact_alt") ?? "").trim();
    if (trap) {
      setStatus("success");
      setMessage("已收到你的提交，我们会在三个工作日内回复。");
      form.reset();
      setLength(0);
      return;
    }

    const payload = new URLSearchParams();
    raw.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    setStatus("loading");
    setMessage("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setStatus("error");
        setMessage(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setMessage("已收到你的提交，我们会在三个工作日内回复。");
      form.reset();
      setLength(0);
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="contact" aria-label="联系方式与线索投稿" className="mx-auto max-w-content scroll-mt-24 px-4 py-14 md:px-6 md:py-20">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-5">
          <p className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
            <span className="h-1 w-6 bg-primary-500"></span>
            CONTACT
          </p>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            有线索，或者想聊聊
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600">
            我们欢迎来自行业从业者与知情者的线索投稿。提交时请尽量说明可核查的材料方向，我们会先做基础核实，
            确认后再决定是否成稿。{siteName}不会直接发布未经核实的内容。
          </p>

          <ul className="mt-8 space-y-4">
            {contactChannels.map((item) => (
              <li key={item.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span>
                  <span className="block text-sm font-medium text-foreground-900">{item.label}</span>
                  <span className="mt-1 block text-xs text-foreground-600">{item.value}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
          >
            <h3 className="font-heading text-lg font-bold text-foreground-950">提交线索或留言</h3>
            <p className="mt-2 text-xs text-foreground-500">带 * 的为必填项，我们会在三个工作日内回复。</p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-sm text-foreground-800">
                  称呼 *
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-sm text-foreground-800">
                  邮箱 *
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-type" className="mb-2 block text-sm text-foreground-800">
                联系类型
              </label>
              <select
                id="contact-type"
                name="type"
                defaultValue="线索投稿"
                className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
              >
                <option value="线索投稿">线索投稿</option>
                <option value="内容纠错">内容纠错</option>
                <option value="商务合作">商务合作</option>
                <option value="读者反馈">读者反馈</option>
              </select>
            </div>

            <div className="mt-4">
              <div className="mb-2 flex items-center justify-between">
                <label htmlFor="contact-message" className="block text-sm text-foreground-800">
                  线索或留言 *
                </label>
                <span className="text-xs text-foreground-500">
                  {length}/{MESSAGE_LIMIT}
                </span>
              </div>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={MESSAGE_LIMIT}
                rows={6}
                onChange={(event) => setLength(event.target.value.length)}
                placeholder="简要说明事件概要、可核查的材料方向，以及你希望我们关注的点。"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>
            </div>

            <input
              className="field-secret-trap"
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-6 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-foreground-950 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "loading" ? "提交中…" : "提交"}
              <i className="ri-send-plane-line"></i>
            </button>

            {status === "success" && (
              <p className="mt-4 flex items-start gap-2 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                  <i className="ri-checkbox-circle-line"></i>
                </span>
                {message}
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
                  <i className="ri-error-warning-line"></i>
                </span>
                {message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}